import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useAuth } from '../context/AuthContext';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { WebPageShell } from '../components/common/WebPageShell';
import { formatLocalTime } from '../utils/dateUtils';
import { useTheme, ThemeColors } from '../context/ThemeContext';

interface LiveClass {
  id: string;
  subject: string;
  teacher: string;
  startTime: string;
  endTime: string;
  status: 'live' | 'upcoming' | 'completed';
  joinUrl: string;
}

export function LiveClassScreen() {
  const { selectedStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [classes, setClasses] = useState<LiveClass[]>([
    {
      id: '1',
      subject: 'Mathematics',
      teacher: 'Dr. Ahmed Khan',
      startTime: new Date(Date.now() + 30 * 60000).toISOString(),
      endTime: new Date(Date.now() + 90 * 60000).toISOString(),
      status: 'upcoming',
      joinUrl: 'https://meet.example.com/math-class',
    },
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live': return '#EF4444';
      case 'upcoming': return '#F59E0B';
      default: return '#94A3B8';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'live': return 'video';
      case 'upcoming': return 'clock';
      default: return 'check-circle';
    }
  };

  const handleJoinClass = (joinUrl: string) => {
    // In a real app, this would open the meeting URL
    console.log('Joining class:', joinUrl);
  };

  return (
    <WebPageShell maxWidth={760}>
      <Text style={styles.title}>Live Classes</Text>

      <ScrollView showsVerticalScrollIndicator={false}>
        {classes.length === 0 ? (
          <View style={styles.empty}>
            <FeatherIcon name="video-off" size={48} color={colors.textMuted} />
            <Text style={styles.emptyText}>No live classes available</Text>
          </View>
        ) : (
          classes.map(cls => (
            <View key={cls.id} style={styles.card}>
              <View style={styles.cardHeader}>
                <View>
                  <Text style={styles.subject}>{cls.subject}</Text>
                  <Text style={styles.teacher}>{cls.teacher}</Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(cls.status) }]}>
                  <FeatherIcon name={getStatusIcon(cls.status) as any} size={14} color="#FFF" />
                  <Text style={styles.statusText}>{cls.status}</Text>
                </View>
              </View>

              <View style={styles.timeInfo}>
                <FeatherIcon name="clock" size={16} color={colors.textMuted} />
                <Text style={styles.timeText}>
                  {formatLocalTime(cls.startTime)} - {formatLocalTime(cls.endTime)}
                </Text>
              </View>

              {cls.status !== 'completed' && (
                <TouchableOpacity
                  style={[styles.joinBtn, cls.status === 'live' && styles.joinBtnLive]}
                  onPress={() => handleJoinClass(cls.joinUrl)}>
                  <FeatherIcon name="link-2" size={16} color="#FFF" />
                  <Text style={styles.joinBtnText}>
                    {cls.status === 'live' ? 'Join Now' : 'Join When Live'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          ))
        )}
      </ScrollView>
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  title: { fontSize: 22, fontWeight: '800', color: colors.textPrimary, marginBottom: 16 },
  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60 },
  emptyText: { fontSize: 16, color: colors.textMuted, marginTop: 12, fontWeight: '600' },
  card: { backgroundColor: colors.card, borderRadius: 12, padding: 16, marginBottom: 12, elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 } },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  subject: { fontSize: 15, fontWeight: '700', color: colors.textPrimary, marginBottom: 2 },
  teacher: { fontSize: 12, color: colors.textMuted },
  statusBadge: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6 },
  statusText: { fontSize: 11, color: '#FFF', fontWeight: '700', textTransform: 'capitalize' },
  timeInfo: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  timeText: { fontSize: 12, color: colors.textSecondary },
  joinBtn: { backgroundColor: colors.primary, paddingVertical: 10, borderRadius: 8, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  joinBtnLive: { backgroundColor: '#EF4444' },
  joinBtnText: { fontSize: 13, fontWeight: '700', color: '#FFF' },
});
